import type { Locale } from "@llmgateway/i18n";

const publicPageCopy = {
	en: {
		pricing: {
			metadata: {
				title: "LLM API Pricing — Per-Token, No Markup",
				description:
					"Pay per-token at provider rates with no markup. Free tier included, volume discounts on paid plans, and one bill across OpenAI, Anthropic, Google, and 30+ providers.",
				twitterDescription:
					"Pay per-token at provider rates with no markup. Free tier included, volume discounts on paid plans, and one bill across 30+ LLM providers.",
			},
		},
		tokenCalculator: {
			metadata: {
				title: "LLM Token Cost Calculator — Compare AI API Pricing",
				description:
					"Free LLM token cost calculator. Estimate and compare API pricing for GPT-4o, Claude, Gemini, and 200+ models, then see how much you save with doteb's cheapest-provider routing and zero platform markup.",
				keywords: [
					"LLM token cost calculator",
					"AI API pricing calculator",
					"token cost calculator",
					"GPT-4o pricing",
					"Claude API cost",
					"Gemini API pricing",
					"LLM pricing comparison",
					"cheapest LLM API",
				],
				openGraphDescription:
					"Estimate token costs across GPT-4o, Claude, Gemini, and 200+ models and compare official pricing against doteb's cheapest-provider routing.",
				twitterDescription:
					"Estimate and compare token costs across 200+ LLMs, then see how much you save with doteb.",
			},
			schema: {
				home: "Home",
				pageName: "Token Cost Calculator",
				appName: "LLM Token Cost Calculator",
				appDescription:
					"Free calculator to estimate and compare LLM token costs across GPT-4o, Claude, Gemini, and 200+ models, with cheapest-provider routing from doteb.",
				faq: [
					{
						question: "How is the cost of LLM tokens calculated?",
						answer:
							"Providers bill separately for input tokens (your prompt) and output tokens (the model's response), priced per million tokens. Your total cost is (input tokens x input price) + (output tokens x output price). This calculator runs that math for every model you add and sums the result.",
					},
					{
						question: "What is the difference between input and output tokens?",
						answer:
							"Input tokens are everything you send to the model, including your prompt, system message, and conversation history. Output tokens are what the model generates back. Output tokens almost always cost more than input tokens, which is why the split matters when you estimate spend.",
					},
					{
						question:
							"Why do the same model's prices differ between providers?",
						answer:
							"Popular models are often served by several providers at different rates, and prices change as providers compete. doteb routes each request to the cheapest available provider for that model, so you pay the lowest live rate without changing any code.",
					},
					{
						question: "Does doteb add a markup or platform fee?",
						answer:
							"No. doteb passes through provider pricing with zero platform markup, so you pay exactly what the provider charges (and less when a cheaper provider or volume discount is available). You only add a payment method once you start sending real traffic.",
					},
					{
						question: "How accurate are these cost estimates?",
						answer:
							"Estimates use current published per-token prices for each model and provider. Real-world cost depends on your exact token counts, caching, and any negotiated rates, so treat the numbers as a close planning estimate rather than a final invoice.",
					},
					{
						question:
							"What is the cheapest way to call LLMs like GPT-4o, Claude, and Gemini?",
						answer:
							"Route through a gateway that compares providers and picks the lowest price per request. Because doteb supports 200+ models behind one OpenAI-compatible API, you can switch models or providers based on cost without rewriting your integration.",
					},
					{
						question: "Is the token cost calculator free to use?",
						answer:
							"Yes, the calculator is completely free and requires no signup. You can compare as many models and token volumes as you like, then create a free doteb account when you are ready to start sending requests.",
					},
				],
			},
		},
		listPages: {
			agents: {
				metadata: {
					title: "AI Agents — Pre-built Tool-Calling Agents",
					description:
						"Pre-built AI agents ready to integrate into your applications. Weather, search, and more.",
				},
			},
			integrations: {
				metadata: {
					title: "Integrations — Claude Code, Cursor, Cline, n8n",
					description:
						"Connect doteb with your favorite tools. Integrate with Claude Code, Cursor, Cline, n8n, and more.",
				},
			},
			templates: {
				metadata: {
					title: "AI App Templates — Production-Ready Starters",
					description:
						"Production-ready templates to jumpstart your AI applications. Image generation, chatbots, and more.",
				},
			},
		},
		apps: {
			pageMetadata: {
				title: "Apps using doteb",
				description:
					"Browse coding agents and tools sending traffic through doteb, ranked by tokens processed. Claude Code, Cursor, Cline, OpenCode, Aider, Continue, and more.",
				openGraphDescription:
					"Coding agents and tools running on doteb, ranked by tokens processed.",
			},
		},
	},
	"zh-CN": {
		pricing: {
			metadata: {
				title: "LLM API 价格 - 按 Token 计费，无加价",
				description:
					"按供应商费率逐 token 付费，无平台加价。包含免费额度，付费方案支持批量折扣，并可在一张账单中覆盖 OpenAI、Anthropic、Google 和 30+ 供应商。",
				twitterDescription:
					"按供应商费率逐 token 付费，无平台加价。包含免费额度，付费方案支持批量折扣，并可在一张账单中覆盖 30+ LLM 供应商。",
			},
		},
		tokenCalculator: {
			metadata: {
				title: "LLM Token 成本计算器 - 比较 AI API 价格",
				description:
					"免费的 LLM token 成本计算器。估算并比较 GPT-4o、Claude、Gemini 和 200+ 模型的 API 价格，并查看通过 doteb 最低价供应商路由和零平台加价可节省多少。",
				keywords: [
					"LLM token 成本计算器",
					"AI API 价格计算器",
					"token 成本计算器",
					"GPT-4o 价格",
					"Claude API 成本",
					"Gemini API 价格",
					"LLM 价格对比",
					"最低价 LLM API",
				],
				openGraphDescription:
					"估算 GPT-4o、Claude、Gemini 和 200+ 模型的 token 成本，并将官方价格与 doteb 最低价供应商路由进行比较。",
				twitterDescription:
					"估算并比较 200+ LLM 的 token 成本，并查看使用 doteb 可节省多少。",
			},
			schema: {
				home: "首页",
				pageName: "Token 成本计算器",
				appName: "LLM Token 成本计算器",
				appDescription:
					"免费估算并比较 GPT-4o、Claude、Gemini 和 200+ 模型的 LLM token 成本，并使用 doteb 最低价供应商路由进行对比。",
				faq: [
					{
						question: "LLM token 成本如何计算？",
						answer:
							"供应商会分别按输入 token（你的 prompt）和输出 token（模型响应）计费，通常按每百万 token 报价。总成本 = 输入 token x 输入单价 + 输出 token x 输出单价。这个计算器会对你添加的每个模型执行这项计算并汇总结果。",
					},
					{
						question: "输入 token 和输出 token 有什么区别？",
						answer:
							"输入 token 是你发送给模型的所有内容，包括 prompt、system message 和会话历史。输出 token 是模型返回生成的内容。输出 token 几乎总是比输入 token 更贵，因此估算花费时必须区分两者。",
					},
					{
						question: "为什么同一模型在不同供应商价格不同？",
						answer:
							"热门模型通常由多个供应商以不同费率提供，价格也会随着竞争变化。doteb 会把每个请求路由到该模型当前可用的最低价供应商，因此无需改代码也能按最低实时价格付费。",
					},
					{
						question: "doteb 会增加加价或平台费吗？",
						answer:
							"不会。doteb 按供应商价格透传，零平台加价，因此你支付的就是供应商收费；当存在更低价供应商或批量折扣时还会更低。只有在开始发送真实流量时才需要添加支付方式。",
					},
					{
						question: "这些成本估算有多准确？",
						answer:
							"估算使用每个模型和供应商当前公开的逐 token 价格。真实成本取决于你的精确 token 数、缓存情况和任何已协商费率，因此这些数字适合作为接近真实账单的规划估算，而不是最终发票。",
					},
					{
						question:
							"调用 GPT-4o、Claude、Gemini 等 LLM 的最低成本方式是什么？",
						answer:
							"通过能够比较供应商并为每个请求选择最低价格的 gateway 路由。由于 doteb 通过一个 OpenAI 兼容 API 支持 200+ 模型，你可以按成本切换模型或供应商，而无需重写集成。",
					},
					{
						question: "Token 成本计算器可以免费使用吗？",
						answer:
							"可以。这个计算器完全免费且无需注册。你可以比较任意数量的模型和 token 用量，并在准备开始发送请求时创建免费的 doteb 账号。",
					},
				],
			},
		},
		listPages: {
			agents: {
				metadata: {
					title: "AI Agents - 预构建工具调用 Agent",
					description:
						"可直接集成到应用中的预构建 AI agent。包含天气、搜索等能力。",
				},
			},
			integrations: {
				metadata: {
					title: "集成 - Claude Code、Cursor、Cline、n8n",
					description:
						"把 doteb 连接到你常用的工具。支持 Claude Code、Cursor、Cline、n8n 等集成。",
				},
			},
			templates: {
				metadata: {
					title: "AI 应用模板 - 生产可用启动模板",
					description:
						"生产可用的模板，可帮助你快速启动 AI 应用。包括图像生成、Chatbot 等。",
				},
			},
		},
		apps: {
			pageMetadata: {
				title: "使用 doteb 的应用",
				description:
					"浏览正在通过 doteb 发送流量的编码 agent 和工具，并按处理 token 数排名。包括 Claude Code、Cursor、Cline、OpenCode、Aider、Continue 等。",
				openGraphDescription:
					"正在 doteb 上运行的编码 agent 和工具，并按处理 token 数排名。",
			},
			descriptions: {
				claudeCode:
					"Anthropic 官方 CLI。通过两个环境变量接入 doteb，即可使用任意模型，而不只限 Claude。",
				cursor:
					"AI 代码编辑器。自定义 OpenAI base URL 可将 Cursor 指向 doteb，实现统一账单。",
				cline: "VS Code 中的自主编码 agent。填入 DevPass key，即可开始交付。",
				codex:
					"OpenAI 的开源终端编码 agent。可与任意 doteb 模型一起使用。",
				opencode:
					"原生 doteb 集成。运行 `opencode`、`/connect`，粘贴你的 DevPass key。",
				aider: "终端中的 AI 结对编程工具。可在本地 git repo 中编辑文件。",
				continue:
					"面向 VS Code 和 JetBrains 的开源 AI 助手。通过一行配置即可切换模型。",
				windsurf:
					"Codeium 的 agentic 编辑器。把 doteb 作为即插即用的 OpenAI 兼容 endpoint。",
				rooCline:
					"具备更强自主能力的 Cline 分支。同一个 DevPass key，同一个固定价格。",
				kiloCode:
					"面向 VS Code 的开源 AI coding agent。可通过 doteb 使用任意供应商。",
				kimiCode:
					"Moonshot AI 的开源 AI coding agent CLI。指向 doteb 即可使用任意模型编码。",
				mimoCode:
					"小米的 AI 编码 agent CLI。指向 doteb 即可使用任意模型编码。",
				zed: "带 Assistant 的高性能编辑器。配置自定义 OpenAI endpoint 即可接入。",
				boltNew: "StackBlitz 的浏览器内 AI 应用构建器。可生成并运行全栈应用。",
				v0: "Vercel 的 AI UI 生成器。可从 prompt 生成生产可用的 React 组件。",
				lovable: "浏览器内全栈 AI 构建器。通过一个聊天线程交付应用。",
				autohand: "浏览器内编码 agent。通过单个 prompt 交付可运行应用。",
				soulforge:
					"Graph-powered coding agent。把代码视为结构而不是字符串，多轮编码会话可显著节省 token。",
				openclaw: "开源 agent 框架，原生支持 doteb。",
				n8n: "工作流自动化平台。可在任何工作流中加入 doteb 节点。",
			},
		},
	},
} as const satisfies Record<Locale, PublicPageCopy>;

interface PublicPageCopy {
	pricing: {
		metadata: {
			title: string;
			description: string;
			twitterDescription: string;
		};
	};
	tokenCalculator: {
		metadata: {
			title: string;
			description: string;
			keywords: string[];
			openGraphDescription: string;
			twitterDescription: string;
		};
		schema: {
			home: string;
			pageName: string;
			appName: string;
			appDescription: string;
			faq: Array<{ question: string; answer: string }>;
		};
	};
	listPages: {
		agents: PageMetadataCopy;
		integrations: PageMetadataCopy;
		templates: PageMetadataCopy;
	};
	apps: {
		pageMetadata: {
			title: string;
			description: string;
			openGraphDescription: string;
		};
		descriptions?: Record<string, string>;
	};
}

interface PageMetadataCopy {
	metadata: {
		title: string;
		description: string;
	};
}

export function getPublicPageCopy(locale: Locale): PublicPageCopy {
	return publicPageCopy[locale] ?? publicPageCopy.en;
}
